import { FallbackProvider, JsonRpcProvider, Web3Provider } from '@ethersproject/providers';
import { type HttpTransport } from 'viem';

import type { PublicClient, WalletClient } from 'wagmi';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { useConnectedAuth } from '@context/auth/connected';
import { chainFromId } from '@utils/chain';

export type LegacyProvider = ReturnType<typeof publicClientToProvider>;
export type LegacySigner = ReturnType<typeof walletClientToSigner>;

function publicClientToProvider(publicClient: PublicClient, chainId: number) {
  const { chain = chainFromId(chainId), transport } = publicClient;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress:
      chain.contracts && 'ensRegistry' in chain.contracts
        ? chain.contracts.ensRegistry?.address
        : undefined,
  };
  if (transport.type === 'fallback')
    return new FallbackProvider(
      (transport.transports as ReturnType<HttpTransport>[]).map(
        ({ value }) => new JsonRpcProvider(value?.url, network),
      ),
    );
  return new JsonRpcProvider(transport.url, network);
}

export function walletClientToSigner(walletClient: WalletClient, chainId: number) {
  const { account, chain = chainFromId(chainId), transport } = walletClient;
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress:
      chain.contracts && 'ensRegistry' in chain.contracts
        ? chain.contracts.ensRegistry?.address
        : undefined,
  };
  const provider = new Web3Provider(transport, network);
  const signer = provider.getSigner(account.address);
  return signer;
}

export function useLegacyEthers() {
  const { getSigner, provider } = useConnectedAuth();

  const getLegacySigner = useStableCallback(async (chainId: number) => {
    const walletClient = await getSigner(chainId);

    return walletClientToSigner(walletClient, chainId);
  });

  const getLegacyProvider = useStableCallback((chainId: number) => {
    const publicClient = provider(chainId);

    return publicClientToProvider(publicClient, chainId);
  });

  return {
    getLegacySigner,
    getLegacyProvider,
  };
}
